import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Spin, Switch, SelectProps } from 'antd';
import { toast } from 'react-toastify';
import { CustomFieldCheckboxRule, CustomFieldDefaultRule, CustomFieldDropdownRule, CustomFieldDto, CustomFieldEnum, ICustomFieldRule } from '@common-types';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

const { Option } = Select;

interface IProps {
  isOpen: boolean;
  setIsOpen: any;
  dptRoles: any;
  fieldData: CustomFieldDto;
  onSaveSuccess: any;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 20 },
  },
};
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 0 },
  },
};

const AddCustomField = (props: IProps) => {
  const { isOpen, setIsOpen, dptRoles, fieldData, onSaveSuccess } = props;
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [typeRule, setTypeRule] = useState<ICustomFieldRule>(new CustomFieldDefaultRule());

  const onFinish = async (values: CustomFieldDto) => {
    values.id = fieldData?.id;
    setLoading(true);
    try {
      await onSaveSuccess(values);
      setIsOpen(false);
    } catch (error) {
      console.error('Error parsing hotels data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fieldData) {
      form.setFieldsValue(fieldData);
    }

    if (fieldData?.type) {
      const selectedType = Number(fieldData.type);
      setTypeRule(new CustomFieldDefaultRule())
      if (selectedType == CustomFieldEnum.Checkbox) {
        setTypeRule(new CustomFieldCheckboxRule());
      }
      if (selectedType == CustomFieldEnum.Dropdown) {
        setTypeRule(new CustomFieldDropdownRule());
      }
    }
  }, [fieldData]);

  const getType = (type: any) => {
    let iconText = 'bi-cursor-text';
    if (type === CustomFieldEnum.Checkbox.toString()) {
      iconText = 'bi-check2-square';
    }
    if (type === CustomFieldEnum.Dropdown.toString()) {
      iconText = 'bi-menu-button-wide';
    }
    return <><i className={`bi ${iconText}`}></i> {` ${CustomFieldEnum[type]}`}</>;
  };

  const intl = useIntl();
  return (
    <Modal
      open={isOpen}
      title={`${intl.formatMessage({ id: 'custom.field' })}`}
      onCancel={() => setIsOpen(false)}
      className='modal-custom-field'
      width={650}
      footer={[
        <div className='p-2'>
          <Button key="cancel" onClick={() => setIsOpen(false)}><FormattedMessage id="cancel" /></Button>
          <Button key="submit" type="primary" onClick={form.submit} loading={loading}><FormattedMessage id="save.button" /></Button>
        </div>
      ]}
    >
      <Spin spinning={loading}>
        <Form form={form} initialValues={{ options: fieldData?.options }} onFinish={onFinish} layout="vertical">
          <div className="flex-container">
            <Form.Item
              label={intl.formatMessage({ id: 'field.type' })}
              name="type"
              className='w-50'
              rules={[{ required: true, message: intl.formatMessage({ id: 'field.type.message' }) }]}
            >
              <span className='form-control form-control-lg'>{getType(fieldData.type)}</span>
            </Form.Item>

            <Form.Item
              label={intl.formatMessage({ id: 'field.label' })}
              name="label"
              className='w-100'
              rules={[{ required: true, message: intl.formatMessage({ id: 'select.label' }) }]}
            >
              <Input placeholder={`${intl.formatMessage({ id: 'field.label' })}`} className='form-control form-control-lg' />
            </Form.Item>
          </div>

          <Form.Item
            label={intl.formatMessage({ id: 'field.help' })}
            name="helpText"
            rules={[{ required: true, message: intl.formatMessage({ id: 'field.help.message' }) }]}
          >
            <Input placeholder={`${intl.formatMessage({ id: 'field.help' })}`} className='form-control form-control-lg' />
          </Form.Item>

          <div className="flex-container">
            {typeRule.showMin ?
              <Form.Item
                label={intl.formatMessage({ id: 'field.minimum' })}
                name="minimum"
                className='w-100'
                rules={[{ required: typeRule.requiredMin, message: intl.formatMessage({ id: 'select.minimum' }) }]}
              >
                <Input type='number' placeholder={`${intl.formatMessage({ id: 'field.minimum' })}`} className='form-control form-control-lg' />
              </Form.Item>
              : null
            }
            {typeRule.showMax ?
              <Form.Item
                label={intl.formatMessage({ id: 'field.maximum' })}
                name="maximum"
                className='w-100'
                rules={[{ required: typeRule.requiredMax, message: intl.formatMessage({ id: 'select.maximum' }) }]}
              >
                <Input type='number' placeholder={`${intl.formatMessage({ id: 'field.maximum' })}`} className='form-control form-control-lg' />
              </Form.Item>
              : null
            }
          </div>

          <div className="flex-container">
            <Form.Item
              className='w-100'
              label={intl.formatMessage({ id: 'visibility' })}
              name="visibility"
              rules={[{ required: true, message: intl.formatMessage({ id: 'select.visibility.message' }) }]}
            >
              <Select placeholder={intl.formatMessage({ id: 'select.visibility' })} mode="multiple" allowClear className='customSelect'>
                <Option key={0} value={'ALL'}>All</Option>
                {dptRoles?.items?.map((role: any, index: number) => (
                  <>
                    <Option key={index + 1} value={role.name}>{role?.description}</Option>
                  </>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              className='w-100'
              label={intl.formatMessage({ id: 'required' })}
              name="mandatory"
              valuePropName="checked"
              rules={[{ required: true }]}
            >
              <Switch />
            </Form.Item>
          </div>

          {typeRule.showSelectAll ?
            <div className="flex-container">
              <Form.Item
                className='w-100'
                label='Enable the "Select All" option'
                name="selectAll"
                valuePropName="selectAll"
              >
                <Switch />
              </Form.Item>
            </div>
            : null
          }

          {typeRule.showMultiSelect ?
            <div className="flex-container">
              <Form.Item
                className='w-100'
                label='Enable the "multiple selection" option'
                name="multiSelect"
                valuePropName="multiSelect"
              >
                <Switch />
              </Form.Item>
            </div>
            : null
          }

          {typeRule.showOptions ?
            <Form.List
              name="options"
              rules={[
                {
                  validator: async (_, names) => {
                    if (!names || names.length < 2) {
                      return Promise.reject(new Error(intl.formatMessage({ id: 'add.two.options' })));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, index) => (
                    <Form.Item
                      {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                      label={index === 0 ? 'Options' : ''}
                      required={true}
                      key={field.key}
                    >
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: intl.formatMessage({ id: 'option.name.message' }),
                          },
                        ]}
                        noStyle
                      >
                        <Input placeholder={`${intl.formatMessage({ id: 'option.name' })}`} style={{ width: '54%' }} className='form-control' />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <MinusCircleOutlined
                          className="dynamic-delete-button"
                          onClick={() => remove(field.name)}
                        />
                      ) : null}
                    </Form.Item>

                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{ width: '45%' }}
                      icon={<PlusOutlined />}
                    >
                      <FormattedMessage id="add.option" />
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>
            : null
          }
        </Form>
      </Spin>
    </Modal>
  );
};

export { AddCustomField };
